// Core packages

// Third party packages

// Custom packages

/**
 * Pure helpers of "Modifica dati aggiuntivi" on the property documents page, when the edited
 * document shares its code (barcode) with other rows.
 *
 * Every row with the same `data.barCode` is a `flatDocuments` row of its own, with its own copy of
 * the additional data, and `PATCH document-archives/:id/update-single-doc` writes exactly one of
 * them. So the page lists the rows of that code, the user ticks the ones that receive the edit, and
 * the same form is saved on each of them, one call per row.
 *
 * The form is filled with the data of the row the edit was opened from, and the back-end merges
 * every field of the form into the row it writes. A row can therefore be ticked only when the form
 * already shows its data: same fields (same data type) and same values. Ticking any other row would
 * overwrite data the user never saw.
 *
 * @since 3.0.0
 */

/** A row of GET document-archives/:id/by-barcode/:barCode (the back-end sends the whole flat row) */
export interface SameCodeDocRow {
  subDocId?: unknown;
  key?: unknown;
  structureObjectId?: unknown;
  structureObjectName?: unknown;
  docTitle?: unknown;
  areaTitle?: unknown;
  subAreaTitle?: unknown;
  deleted?: unknown;
  mockup?: unknown;
  data?: unknown;
}

/** A field of the edit form, as the comparison reads it */
export interface SameCodeFormField {
  key: string;
  /** Formly type: a "datepicker" is compared by day, a "checkbox" as ticked or not */
  type: string;
  /** Translated label, "" when the field has none to show (hidden fields) */
  label: string;
  /** Formly writes it into the model when the value is undefined, so it is what the form shows */
  defaultValue?: unknown;
}

/** Why a row of the selection cannot be ticked */
export type SameCodeLockReason = "differentForm" | "differentData";

/** A row of the selection dialog */
export interface SameCodeChoice {
  /** `subDocId` of the flat row, which is the `_id` of the row on the page */
  subDocId: string;
  key: string;
  /** Structure element of the row as stored, "" when it has none */
  structureObjectId: string;
  docTitle: string;
  /** "<area> >" as the delete dialog prints it, "" when unknown */
  area: string;
  /** "<subarea> >" as the delete dialog prints it, "" when unknown */
  subArea: string;
  structureObjectName: string;
  /** The row the edit was opened from: the form shows its data */
  isClicked: boolean;
  /** null when the row can be ticked */
  lockReason: SameCodeLockReason | null;
  /** differentData only: labels of the fields that differ, in form order */
  differentFields: string[];
  /** differentData only: at least one differing field has no label to show */
  differentUnlabeled: boolean;
}

/** A row the edit is saved on */
export interface SameCodeEditTarget {
  subDocId: string;
  docKey: string;
  /** Route segment of update-single-doc (the back-end reads the type from `docKey`) */
  documentTypeRoute: string;
  /**
   * Structure segment of update-single-doc. `undefined` travels as the literal "undefined", which the
   * back-end reads as "no structure": it then matches the row on key and subDocId alone.
   */
  structureId: string | undefined;
  /** "<title> – <area> > <subarea> > <structure>", for the edit dialog and the error message */
  label: string;
}

/** What `buildSameCodeChoices` needs to know about the clicked row and the document template */
export interface SameCodeChoicesInput {
  /** `data` of the by-barcode response */
  rows: unknown;
  /** `data.barCode` of the clicked row */
  barCode: string;
  /** `_id` of the clicked row on the page, which is the `subDocId` of its flat row */
  clickedSubDocId: string;
  /** Type of the clicked row, e.g. "AA1" */
  clickedKey: string;
  /** Fields of the edit form, see `collectSameCodeFormFields` */
  fields: SameCodeFormField[];
  /** Data type and update-single-doc route of a document type, "" when the template does not know it */
  formOf: (key: string) => { schemaType: string; route: string };
}

/** What `buildSameCodeEditTargets` needs besides the ticked rows */
export interface SameCodeTargetsOptions {
  /** Structure segment the single edit of the clicked row sends, as the page computes it */
  clickedStructureId: string | undefined;
  /** False on FREE/FREEBASIC properties: the page never sends a structure there */
  sendStructureIds: boolean;
  /** update-single-doc route of a document type */
  routeOf: (key: string) => string;
}

/** Signature of `TranslateService.instant` as the lock message uses it */
export type SameCodeTranslate = (key: string, params?: object) => string;

/** How many field names a lock message lists before "N more" */
const MAX_LISTED_FIELDS = 3;

/**
 * A label that is still a translation key: `TranslateService.instant` hands the key back when it
 * has no text for it (e.g. "properties.detail.documentArchive.fields.undefined"). No spaces, at
 * least one dot, a word on both sides of every dot. Linear: each repetition starts with a dot.
 */
const UNTRANSLATED_LABEL = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)+$/;

/** A day as an ISO string without time ("2026-10-02"): the datepicker shows it as it is */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Suffix of the "not available" box that Formly adds after a date (`<date>chkNA`) */
const NOT_AVAILABLE_SUFFIX = /chkNA$/;

/** Nesting deeper than this is compared as raw JSON instead of field by field */
const MAX_DEPTH = 20;

function isRecord(value: unknown): value is { [key: string]: unknown } {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Own property only: a field key never reaches the prototype of the data object */
function readOwn(record: { [key: string]: unknown }, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}

/** Trimmed text of a string or a finite number, "" for anything else */
function textOf(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" && isFinite(value)) {
    return String(value);
  }
  return "";
}

function labelOf(templateOptions: unknown): string {
  if (!isRecord(templateOptions)) {
    return "";
  }
  // Autocomplete types print `customLabel`, every other type prints `label`
  const label = textOf(templateOptions.label) || textOf(templateOptions.customLabel);
  return UNTRANSLATED_LABEL.test(label) ? "" : label;
}

function pad2(value: number): string {
  return (value < 10 ? "0" : "") + value;
}

/** Day of a date in the browser time zone, which is the one the datepicker shows; null if invalid */
function localDayOf(date: Date): string | null {
  if (isNaN(date.getTime())) {
    return null;
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * Canonical text of a value as a form input shows it, null when it shows nothing. Spaces around a
 * text do not count, 5 and "5" read the same, an empty list or an object of empty values is empty.
 * Object keys are sorted, list items keep their order.
 */
function canonicalOf(value: unknown, depth: number): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    const text = value.trim();
    return text !== "" ? text : null;
  }
  if (typeof value === "number") {
    return isFinite(value) ? String(value) : null;
  }
  if (typeof value === "boolean") {
    return String(value);
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (depth > MAX_DEPTH) {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    const items = value.map((item: unknown) => canonicalOf(item, depth + 1));
    return items.some((item) => item !== null) ? JSON.stringify(items) : null;
  }
  if (isRecord(value)) {
    const entries: Array<[string, string]> = [];
    Object.keys(value)
      .sort()
      .forEach((key) => {
        const item = canonicalOf(value[key], depth + 1);
        if (item !== null) {
          entries.push([key, item]);
        }
      });
    return entries.length ? JSON.stringify(entries) : null;
  }
  // Functions and symbols never come out of JSON: the form shows nothing for them
  return null;
}

/** Canonical day of a datepicker value; a text that is not a date is compared as text */
function dayOf(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    const text = value.trim();
    if (text === "") {
      return null;
    }
    if (DATE_ONLY.test(text)) {
      // new Date() would read a bare day as UTC midnight, i.e. the day before west of Greenwich
      return text;
    }
    const day = localDayOf(new Date(text));
    return day !== null ? day : text;
  }
  if (typeof value === "number") {
    const day = localDayOf(new Date(value));
    return day !== null ? day : canonicalOf(value, 0);
  }
  if (value instanceof Date) {
    return localDayOf(value);
  }
  return canonicalOf(value, 0);
}

/**
 * Fields of the edit form, in form order. A keyed field is one value of the model even when it has
 * children, so it is compared as a whole; a group without a key only wraps its children and is
 * walked through. A key that appears twice is kept once.
 *
 * @param fieldConfigs Formly configuration of the form (the page's `fieldGroup`), labels translated
 */
export function collectSameCodeFormFields(fieldConfigs: unknown): SameCodeFormField[] {
  const fields: SameCodeFormField[] = [];
  const seen: { [key: string]: boolean } = Object.create(null);

  const walk = (configs: unknown): void => {
    if (!Array.isArray(configs)) {
      return;
    }
    configs.forEach((config: unknown) => {
      if (!isRecord(config)) {
        return;
      }
      const key = textOf(config.key);
      if (key === "") {
        walk(config.fieldGroup);
        return;
      }
      if (seen[key]) {
        return;
      }
      seen[key] = true;
      fields.push({
        key,
        type: typeof config.type === "string" ? config.type : "",
        label: labelOf(config.templateOptions),
        defaultValue: config.defaultValue,
      });
    });
  };

  walk(fieldConfigs);
  return fields;
}

/**
 * Canonical text of a field value as the form shows it, null when the form shows it empty: an empty
 * text, a missing key and null are the same thing, two dates of the same day are the same date, a
 * box is ticked or not.
 *
 * @param value value stored in the row data under the field key
 * @param field field of the edit form
 */
export function sameCodeComparableValue(value: unknown, field: SameCodeFormField): string | null {
  const shown = value === undefined && field.defaultValue !== undefined ? field.defaultValue : value;
  if (field.type === "datepicker") {
    return dayOf(shown);
  }
  if (field.type === "checkbox") {
    // Same reading as MatCheckbox.writeValue
    return shown ? "true" : null;
  }
  return canonicalOf(shown, 0);
}

/**
 * Fields of the form whose value is not the same in the two rows of data.
 *
 * @param fields fields of the edit form
 * @param reference data of the clicked row
 * @param other data of the row to compare
 */
export function findSameCodeDifferences(
  fields: SameCodeFormField[],
  reference: unknown,
  other: unknown
): SameCodeFormField[] {
  const referenceData = isRecord(reference) ? reference : {};
  const otherData = isRecord(other) ? other : {};
  return fields.filter(
    (field) =>
      sameCodeComparableValue(readOwn(referenceData, field.key), field) !==
      sameCodeComparableValue(readOwn(otherData, field.key), field)
  );
}

/**
 * Labels to print for the differing fields. A field without a label of its own belongs to a visible
 * one: the "not available" box to its date (`<date>chkNA`, whose own label would only say "not
 * available"), the hidden supplier name to the supplier picker (`<name>Id`).
 *
 * Exported for the same selection on the 3.0.0 archive (`document-archive-edit.utils.ts`).
 */
export function differenceLabels(
  different: SameCodeFormField[],
  fields: SameCodeFormField[]
): { labels: string[]; unlabeled: boolean } {
  const labelByKey: { [key: string]: string } = Object.create(null);
  fields.forEach((field) => {
    labelByKey[field.key] = field.label;
  });

  const labels: string[] = [];
  let unlabeled = false;
  different.forEach((field) => {
    const dateLabel = NOT_AVAILABLE_SUFFIX.test(field.key)
      ? labelByKey[field.key.replace(NOT_AVAILABLE_SUFFIX, "")] || ""
      : "";
    const label = dateLabel || field.label || labelByKey[`${field.key}Id`] || "";
    if (label === "") {
      unlabeled = true;
    } else if (labels.indexOf(label) === -1) {
      labels.push(label);
    }
  });
  return { labels, unlabeled };
}

/**
 * Rows of the selection dialog, in the back-end order, or null when the edit has to stay on the
 * clicked row alone: no code, the clicked row is not among the rows of its code (deleted, mockup,
 * a lookup that does not match), or its type is not in the document template.
 *
 * Only the rows whose code is exactly the clicked one are kept, regardless of case and outer
 * spaces, because by-barcode matches the code as a case-insensitive substring. Deleted rows (left
 * inside a deleted structure) and mockups are left out, as the page hides them from editing.
 *
 * Nothing is ticked: the choice is the user's.
 */
export function buildSameCodeChoices(input: SameCodeChoicesInput): SameCodeChoice[] | null {
  const code = textOf(input.barCode).toUpperCase();
  if (code === "" || !Array.isArray(input.rows)) {
    return null;
  }

  const rows: SameCodeDocRow[] = [];
  const seen: { [subDocId: string]: boolean } = Object.create(null);
  input.rows.forEach((row: unknown) => {
    if (!isRecord(row) || row.deleted === true || row.mockup === true) {
      return;
    }
    const subDocId = textOf(row.subDocId);
    const data = isRecord(row.data) ? row.data : {};
    if (
      subDocId === "" ||
      textOf(row.key) === "" ||
      seen[subDocId] ||
      textOf(data.barCode).toUpperCase() !== code
    ) {
      return;
    }
    seen[subDocId] = true;
    rows.push(row as SameCodeDocRow);
  });

  const clickedSubDocId = textOf(input.clickedSubDocId);
  const clickedKey = textOf(input.clickedKey);
  const clicked = rows.filter(
    (row) => textOf(row.subDocId) === clickedSubDocId && textOf(row.key) === clickedKey
  )[0];
  if (!clicked) {
    return null;
  }
  const clickedForm = input.formOf(clickedKey);
  if (clickedForm.route === "") {
    return null;
  }

  return rows.map((row) => {
    const key = textOf(row.key);
    const isClicked = row === clicked;
    let lockReason: SameCodeLockReason | null = null;
    let differences: { labels: string[]; unlabeled: boolean } = { labels: [], unlabeled: false };

    if (!isClicked) {
      const form = input.formOf(key);
      if (form.route === "" || form.schemaType !== clickedForm.schemaType) {
        lockReason = "differentForm";
      } else {
        const different = findSameCodeDifferences(input.fields, clicked.data, row.data);
        if (different.length) {
          lockReason = "differentData";
          differences = differenceLabels(different, input.fields);
        }
      }
    }

    const areaTitle = textOf(row.areaTitle);
    const subAreaTitle = textOf(row.subAreaTitle);
    return {
      subDocId: textOf(row.subDocId),
      key,
      structureObjectId: textOf(row.structureObjectId),
      docTitle: textOf(row.docTitle),
      area: areaTitle !== "" ? `${areaTitle} >` : "",
      subArea: subAreaTitle !== "" ? `${subAreaTitle} >` : "",
      structureObjectName: textOf(row.structureObjectName),
      isClicked,
      lockReason,
      differentFields: differences.labels,
      differentUnlabeled: differences.unlabeled,
    };
  });
}

/**
 * Text under a row that cannot be ticked, "" for a row that can.
 *
 * @param choice row of the selection dialog
 * @param translate `TranslateService.instant`
 */
export function sameCodeLockMessage(choice: SameCodeChoice, translate: SameCodeTranslate): string {
  if (choice.lockReason === "differentForm") {
    return translate("properties.dialog.docEditSameCodeDifferentForm");
  }
  if (choice.lockReason !== "differentData") {
    return "";
  }
  if (!choice.differentFields.length) {
    return translate("properties.dialog.docEditSameCodeDifferentDataNoFields");
  }
  const names = choice.differentFields.slice();
  if (choice.differentUnlabeled) {
    names.push(translate("properties.dialog.docEditSameCodeOtherData"));
  }
  const listed =
    names.length > MAX_LISTED_FIELDS
      ? names
          .slice(0, MAX_LISTED_FIELDS)
          .concat(
            translate("properties.dialog.docEditSameCodeOtherFields", {
              count: names.length - MAX_LISTED_FIELDS,
            })
          )
      : names;
  return translate("properties.dialog.docEditSameCodeDifferentData", { fields: listed.join(", ") });
}

/** "<title> – <area> > <subarea> > <structure>", the way the selection dialog prints the row */
export function sameCodeChoiceLabel(choice: SameCodeChoice): string {
  const place = [choice.area, choice.subArea, choice.structureObjectName]
    .filter((part) => part !== "")
    .join(" ");
  return [choice.docTitle || choice.key, place].filter((part) => part !== "").join(" – ");
}

/**
 * Rows the edit is saved on. Locked rows are dropped even if the caller passes them.
 *
 * The clicked row keeps exactly the structure segment its single edit sends. For the others the
 * stored structure element is the one the page passes too when they are clicked (structure blocks
 * carry the `structureObjectId` of their rows, the other sections carry none and so do their rows),
 * so the back-end matches each row and computes its expiry on the same branch as a single edit of it.
 * That stored value becomes a path segment, so it is encoded: a "/" or ".." in it cannot reach
 * another route (an ObjectId or "-1" comes out unchanged).
 *
 * @param selected rows ticked in the selection dialog
 * @param options clicked row segment, subscription rule and document template lookup
 */
export function buildSameCodeEditTargets(
  selected: SameCodeChoice[],
  options: SameCodeTargetsOptions
): SameCodeEditTarget[] {
  return selected
    .filter((choice) => choice.lockReason === null)
    .map((choice) => {
      let structureId: string | undefined;
      if (choice.isClicked) {
        structureId = options.clickedStructureId;
      } else if (options.sendStructureIds && choice.structureObjectId !== "") {
        structureId = encodeURIComponent(choice.structureObjectId);
      }
      return {
        subDocId: choice.subDocId,
        docKey: choice.key,
        documentTypeRoute: options.routeOf(choice.key),
        structureId,
        label: sameCodeChoiceLabel(choice),
      };
    });
}
